import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  adicionarCorda,
  atualizarCorda as atualizarCordaDominio,
  inverterSentidoCorda,
  moverPontaDaCorda,
  moverPontoDoPercurso,
  definirFolgaDoTrecho,
  alinharPoliasDaCorda,
  removerCorda as removerCordaDominio,
  criarAncoragemAutomatica,
  amarrarPonta,
  pontasQuePedemNo,
  type AtualizacoesCorda,
  type LadoDaPonta,
  type NoNaPonta,
  type CenaTabletop,
  type CordaTabletop,
  type FuncaoCorda,
  type ReferenciaPonto,
} from '../../dominio/tabletop'

/** A corda que está sendo desenhada: a função escolhida e os pontos clicados até agora. */
export interface DesenhoCorda {
  readonly funcao: FuncaoCorda
  readonly percurso: readonly ReferenciaPonto[]
}

const mesmaReferencia = (a: ReferenciaPonto | undefined, b: ReferenciaPonto) =>
  a !== undefined && a.idObjeto === b.idObjeto && a.idPonto === b.idPonto

/**
 * Estado da ferramenta de corda: desenhar (função + pontos clicados em
 * sequência), selecionar, editar e apagar.
 *
 * O desenho em andamento NÃO entra na cena nem no histórico — só a corda
 * concluída entra, num único passo de desfazer. A seleção da corda fica aqui
 * e cede à do objeto: escolher um objeto tira a seleção da corda.
 */
export function useCordasTabletop({
  cena,
  aplicar,
  gerarId,
  idObjetoSelecionado,
  selecionarObjeto,
  cancelarPosicionamento,
  ativo,
  aoIniciarDesenho,
  aoSelecionar,
}: {
  cena: CenaTabletop
  aplicar: (fn: (cena: CenaTabletop) => CenaTabletop) => void
  gerarId: () => string
  idObjetoSelecionado: string | null
  selecionarObjeto: (id: string | null) => void
  cancelarPosicionamento: () => void
  /** Falso quando a cena está travada: não se desenha corda. */
  ativo: boolean
  /** Chamado ao começar a desenhar: o chamador cancela o desenho da ancoragem. */
  aoIniciarDesenho?: () => void
  /** Chamado ao selecionar uma corda: o chamador tira a seleção da ancoragem. */
  aoSelecionar?: () => void
}) {
  const [desenhoCorda, setDesenhoCorda] = useState<DesenhoCorda | null>(null)
  const [idCordaEscolhida, setIdCordaEscolhida] = useState<string | null>(null)
  /** Uma ponta da corda selecionada esperando o toque no novo ponto. */
  const [movendoPonta, setMovendoPonta] = useState<{ idCorda: string; lado: 'inicio' | 'fim' } | null>(null)
  /**
   * As pontas da corda que esperam a escolha do nó: ao levar a corda até um
   * ponto de ancoragem, pergunta-se como ela se prende ali (oito, fiel ou sem nó).
   */
  const [pontasParaAmarrar, setPontasParaAmarrar] = useState<{ idCorda: string; lados: readonly LadoDaPonta[] } | null>(null)

  useEffect(() => {
    if (!ativo) {
      setDesenhoCorda(null)
      setMovendoPonta(null)
      setPontasParaAmarrar(null)
    }
  }, [ativo])
  useEffect(() => {
    // a ponta só se move na corda selecionada
    setMovendoPonta((atual) => (atual && atual.idCorda !== idCordaEscolhida ? null : atual))
  }, [idCordaEscolhida])

  /** Começa uma corda; com `inicio`, ela já nasce presa nesse ponto (tocado antes, na cena). */
  const iniciarCorda = useCallback(
    (funcao: FuncaoCorda, inicio?: ReferenciaPonto) => {
      cancelarPosicionamento()
      selecionarObjeto(null)
      setIdCordaEscolhida(null)
      aoIniciarDesenho?.()
      setDesenhoCorda({ funcao, percurso: inicio ? [inicio] : [] })
    },
    [aoIniciarDesenho, cancelarPosicionamento, selecionarObjeto],
  )

  const trocarFuncaoDoDesenho = useCallback(
    (funcao: FuncaoCorda) => setDesenhoCorda((atual) => (atual ? { ...atual, funcao } : atual)),
    [],
  )

  const adicionarPontoAoDesenho = useCallback((referencia: ReferenciaPonto) => {
    setDesenhoCorda((atual) => {
      if (!atual || mesmaReferencia(atual.percurso[atual.percurso.length - 1], referencia)) return atual
      return { ...atual, percurso: [...atual.percurso, referencia] }
    })
  }, [])

  const removerUltimoPontoDoDesenho = useCallback(
    () => setDesenhoCorda((atual) => (atual ? { ...atual, percurso: atual.percurso.slice(0, -1) } : atual)),
    [],
  )

  const cancelarCorda = useCallback(() => setDesenhoCorda(null), [])

  const selecionarCorda = useCallback(
    (id: string | null) => {
      setIdCordaEscolhida(id)
      if (id) {
        selecionarObjeto(null)
        aoSelecionar?.()
      }
    },
    [aoSelecionar, selecionarObjeto],
  )

  /** Conclui o desenho: a corda entra na cena, num passo só de desfazer, e fica selecionada. */
  const concluirCorda = useCallback((): boolean => {
    if (!desenhoCorda) return false
    const ehFita = desenhoCorda.funcao === 'fita'
    if (ehFita && desenhoCorda.percurso.length === 1) {
      try {
        criarAncoragemAutomatica(cena, desenhoCorda.percurso, undefined, gerarId)
        aplicar((c) => criarAncoragemAutomatica(c, desenhoCorda.percurso, undefined, gerarId).cena)
        // a fita solta fica sem seleção: sem o símbolo do ponto de ancoragem na frente
        setDesenhoCorda(null)
        return true
      } catch {
        setDesenhoCorda(null)
        return false
      }
    }
    if (desenhoCorda.percurso.length < 2) return false
    const id = gerarId()
    try {
      // valida antes de mexer no estado: o desenho pode apontar para algo que já saiu da cena
      adicionarCorda(cena, { funcao: desenhoCorda.funcao, percurso: desenhoCorda.percurso }, () => id)
    } catch {
      setDesenhoCorda(null)
      return false
    }
    // a corda nova acomoda as polias por cujas roldanas passa
    aplicar((c) => alinharPoliasDaCorda(adicionarCorda(c, { funcao: desenhoCorda.funcao, percurso: desenhoCorda.percurso }, () => id), id))
    setDesenhoCorda(null)
    selecionarCorda(id)
    // presa em ponto de ancoragem: pergunta o nó de cada ponta
    const lados = pontasQuePedemNo(adicionarCorda(cena, { funcao: desenhoCorda.funcao, percurso: desenhoCorda.percurso }, () => id), id)
    setPontasParaAmarrar(lados.length > 0 ? { idCorda: id, lados } : null)
    return true
  }, [aplicar, cena, desenhoCorda, gerarId, selecionarCorda])

  /** Conclui o desenho de fita como ancoragem equalizada (V, W, M) com vértice automático. */
  const concluirComoAncoragem = useCallback((): boolean => {
    if (!desenhoCorda || desenhoCorda.percurso.length < 2) return false
    try {
      criarAncoragemAutomatica(cena, desenhoCorda.percurso, undefined, gerarId)
      aplicar((c) => criarAncoragemAutomatica(c, desenhoCorda.percurso, undefined, gerarId).cena)
      setDesenhoCorda(null)
      return true
    } catch {
      setDesenhoCorda(null)
      return false
    }
  }, [aplicar, cena, desenhoCorda, gerarId])

  const iniciarMoverPonta = useCallback((lado: 'inicio' | 'fim') => {
    if (idCordaEscolhida) setMovendoPonta({ idCorda: idCordaEscolhida, lado })
  }, [idCordaEscolhida])
  const cancelarMoverPonta = useCallback(() => setMovendoPonta(null), [])
  /** O novo ponto da ponta em movimento: a corda muda, num passo de desfazer; ponto inválido não muda nada. */
  const moverPontaPara = useCallback(
    (referencia: ReferenciaPonto) => {
      if (!movendoPonta) return
      const { idCorda, lado } = movendoPonta
      aplicar((c) => {
        try {
          return alinharPoliasDaCorda(moverPontaDaCorda(c, idCorda, lado, referencia), idCorda)
        } catch {
          return c
        }
      })
      setMovendoPonta(null)
    },
    [aplicar, movendoPonta],
  )

  /** Arrastar um ponto da corda (ponta ou meio) para outro ponto de ancoragem: um passo de desfazer; ponto inválido não muda nada. */
  const reposicionarPontoDaCorda = useCallback(
    (idCorda: string, indice: number, referencia: ReferenciaPonto) => {
      aplicar((c) => {
        try {
          return alinharPoliasDaCorda(moverPontoDoPercurso(c, idCorda, indice, referencia), idCorda)
        } catch {
          return c
        }
      })
    },
    [aplicar],
  )

  /** Pergunta de novo o nó das pontas da corda (a corda selecionada, por exemplo). */
  const pedirNosDasPontas = useCallback(
    (idCorda: string) => {
      const lados = pontasQuePedemNo(cena, idCorda)
      setPontasParaAmarrar(lados.length > 0 ? { idCorda, lados } : null)
    },
    [cena],
  )
  /** A resposta da pergunta da ponta da vez: o nó (um passo de desfazer) ou `null`, sem nó. Segue para a próxima ponta. */
  const responderNoDaPonta = useCallback(
    (no: NoNaPonta | null) => {
      if (!pontasParaAmarrar) return
      const { idCorda, lados } = pontasParaAmarrar
      const lado = lados[0]!
      if (no) {
        aplicar((c) => {
          try {
            return amarrarPonta(c, idCorda, lado, no, gerarId).cena
          } catch {
            return c
          }
        })
      }
      setPontasParaAmarrar(lados.length > 1 ? { idCorda, lados: lados.slice(1) } : null)
    },
    [aplicar, gerarId, pontasParaAmarrar],
  )
  const cancelarNosDasPontas = useCallback(() => setPontasParaAmarrar(null), [])

  const atualizarCorda = useCallback(
    (id: string, alteracoes: AtualizacoesCorda) => aplicar((c) => atualizarCordaDominio(c, id, alteracoes)),
    [aplicar],
  )
  const inverterSentido = useCallback((id: string) => aplicar((c) => inverterSentidoCorda(c, id)), [aplicar])
  const definirFolga = useCallback(
    (id: string, indice: number, folgaM: number) => aplicar((c) => definirFolgaDoTrecho(c, id, indice, folgaM)),
    [aplicar],
  )
  const removerCorda = useCallback(
    (id: string) => {
      aplicar((c) => removerCordaDominio(c, id))
      setIdCordaEscolhida((atual) => (atual === id ? null : atual))
    },
    [aplicar],
  )

  // a corda some da cena (desfazer, abrir outro arquivo) ou um objeto é escolhido: sem seleção
  const cordaSelecionada: CordaTabletop | null = useMemo(
    () => (idObjetoSelecionado ? null : (cena.cordas?.find((c) => c.id === idCordaEscolhida) ?? null)),
    [cena.cordas, idCordaEscolhida, idObjetoSelecionado],
  )

  return {
    desenhoCorda,
    cordaSelecionada,
    pontaParaAmarrar: pontasParaAmarrar && cena.cordas?.some((c) => c.id === pontasParaAmarrar.idCorda)
      ? { idCorda: pontasParaAmarrar.idCorda, lado: pontasParaAmarrar.lados[0]! }
      : null,
    pedirNosDasPontas,
    responderNoDaPonta,
    cancelarNosDasPontas,
    idCordaSelecionada: cordaSelecionada?.id ?? null,
    iniciarCorda,
    trocarFuncaoDoDesenho,
    movendoPonta,
    iniciarMoverPonta,
    cancelarMoverPonta,
    moverPontaPara,
    reposicionarPontoDaCorda,
    adicionarPontoAoDesenho,
    removerUltimoPontoDoDesenho,
    cancelarCorda,
    concluirCorda,
    concluirComoAncoragem,
    selecionarCorda,
    atualizarCorda,
    inverterSentido,
    definirFolga,
    removerCorda,
  }
}
